import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import { useNotification } from "../../../components/Notification/NotificationManager";
import useScrollToTop from "../../../hooks/useScollToTop";
import { createCheckoutSessionForPlan } from "../../../services/paymentServices";
import { getUserById } from "../../../services/userServices";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import {
  FaArrowRight,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaCrown,
  FaGift,
  FaRocket,
  FaShieldAlt,
  FaStar,
  FaSyncAlt,
  FaUserCircle,
} from "react-icons/fa";

const cn = (...classes) => classes.filter(Boolean).join(" ");

// Ordre métier des formules
const PLAN_ORDER = {
  2: 0, // Demo
  7: 1, // Presence Free
  3: 2, // Presence
  4: 3, // Presence Pro
  5: 4, // Presence Ultim
  1: 99, // Admin
};

const TONE_STYLES = {
  emerald: {
    surface:
      "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-100/80 dark:border-emerald-500/20 dark:from-emerald-500/10 dark:via-slate-900 dark:to-emerald-400/10",
    badge:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
    icon:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
    price: "text-emerald-700 dark:text-emerald-300",
    outline:
      "ring-2 ring-emerald-200 shadow-lg shadow-emerald-500/10 dark:ring-emerald-500/20",
    button:
      "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600",
    soft:
      "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
  blue: {
    surface:
      "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-sky-100/80 dark:border-blue-500/20 dark:from-blue-500/10 dark:via-slate-900 dark:to-sky-400/10",
    badge: "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
    icon: "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
    price: "text-blue-700 dark:text-blue-300",
    outline: "",
    button:
      "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600",
    soft: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
  },
  indigo: {
    surface:
      "border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-100/70 dark:border-indigo-500/20 dark:from-indigo-500/10 dark:via-slate-900 dark:to-violet-400/10",
    badge:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
    icon:
      "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
    price: "text-indigo-700 dark:text-indigo-300",
    outline:
      "ring-2 ring-indigo-200 shadow-lg shadow-indigo-500/10 dark:ring-indigo-500/20",
    button:
      "bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600",
    soft:
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
  },
  violet: {
    surface:
      "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-100/70 dark:border-violet-500/20 dark:from-violet-500/10 dark:via-slate-900 dark:to-fuchsia-400/10",
    badge:
      "bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
    icon:
      "bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
    price: "text-violet-700 dark:text-violet-300",
    outline: "",
    button:
      "bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-600",
    soft:
      "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
  },
};

const plans = [
  {
    name: "Presence Free",
    role_id: 7,
    formule: "Presence Free",
    price: "Gratuit",
    period: "",
    amount: null,
    product_id: null,
    tone: "emerald",
    badge: "Gratuit",
    icon: FaGift,
    featured: true,
    shortDescription: "La formule idéale pour démarrer avec un petit club.",
    highlights: ["50 adhérents max", "1 lieu", "2 groupes"],
    features: [
      "Gestion réelle du club",
      "Jusqu'à 50 adhérents",
      "2 groupes maximum",
      "1 lieu maximum",
    ],
    footer: "Parfait pour commencer sans frais.",
  },
  {
    name: "Presence",
    role_id: 3,
    formule: "Presence",
    price: "10 €",
    period: "/ an",
    amount: 10,
    product_id: "prod_RihyvIWIUVQQp1",
    tone: "blue",
    badge: "Essentiel",
    icon: FaShieldAlt,
    featured: false,
    shortDescription: "Pour un usage complet sur une structure simple.",
    highlights: ["< 100 adhérents", "1 lieu", "Plusieurs groupes"],
    features: [
      "Gestion du club, groupes, lieux et adhérents",
      "Invitations enseignants",
      "Présences, absences et tableau de bord",
      "Limité à 1 lieu et moins de 100 adhérents",
    ],
    footer: "La formule simple et efficace.",
  },
  {
    name: "Presence Pro",
    role_id: 4,
    formule: "Presence Pro",
    price: "20 €",
    period: "/ an",
    amount: 20,
    product_id: "prod_RihzKEqLrd7W0w",
    tone: "indigo",
    badge: "Populaire",
    icon: FaRocket,
    featured: true,
    shortDescription: "Le meilleur équilibre pour les clubs en croissance.",
    highlights: ["200 adhérents max", "3 lieux", "Plusieurs groupes"],
    features: [
      "Tout ce qui est inclus dans Presence",
      "Jusqu'à 3 lieux",
      "Jusqu'à 200 adhérents",
      "Plus de souplesse de gestion",
    ],
    footer: "La formule la plus équilibrée.",
  },
  {
    name: "Presence Ultim",
    role_id: 5,
    formule: "Presence Ultim",
    price: "30 €",
    period: "/ an",
    amount: 30,
    product_id: "prod_Rii0cE4OKYDGV4",
    tone: "violet",
    badge: "Illimité",
    icon: FaCrown,
    featured: false,
    shortDescription: "Pour les clubs les plus complets et les besoins avancés.",
    highlights: ["> 200 adhérents", "Plus de 3 lieux", "Plusieurs groupes"],
    features: [
      "Tout ce qui est inclus dans Presence Pro",
      "Plus de 3 lieux",
      "Plus de 200 adhérents",
      "Adapté aux clubs les plus complets",
    ],
    footer: "La formule la plus complète.",
  },
];

function PricingPage() {
  useScrollToTop();

  const { user, updateUser } = useUser();
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [selectedPlanRoleId, setSelectedPlanRoleId] = useState(4);

  let storedUserInfo = null;
  try {
    storedUserInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    storedUserInfo = null;
  }

  const effectiveUser = user || storedUserInfo || null;
  const userId = effectiveUser?.userId || effectiveUser?.id || null;
  const isAuthenticated = Boolean(userId);

  const currentRoleId = effectiveUser?.role_id ?? null;
  const currentPlanRank = PLAN_ORDER[currentRoleId] ?? -1;

  const pricingPlans = useMemo(
    () => plans.filter((plan) => plan.role_id !== 2),
    []
  );

  const currentPlan = useMemo(() => {
    return plans.find((plan) => plan.role_id === currentRoleId) || null;
  }, [currentRoleId]);

  const selectedPlan = useMemo(() => {
    return (
      pricingPlans.find((plan) => plan.role_id === selectedPlanRoleId) ||
      pricingPlans[0] ||
      null
    );
  }, [pricingPlans, selectedPlanRoleId]);

  const currentRoleLabel = useMemo(() => {
    if (!currentRoleId) return "Non connecté";
    if (currentRoleId === 1) return "Administrateur";
    return getRoleFromRoleId(currentRoleId);
  }, [currentRoleId]);

  useEffect(() => {
    if (localStorage.getItem("userInfo")) {
      try {
        const parsed = JSON.parse(localStorage.getItem("userInfo"));
        if (!parsed || (!parsed.userId && !parsed.id)) {
          localStorage.removeItem("userInfo");
        }
      } catch (error) {
        localStorage.removeItem("userInfo");
      }
    }
  }, []);

  useEffect(() => {
    if (!pricingPlans.length) return;

    const defaultRoleId = pricingPlans.some((p) => p.role_id === currentRoleId)
      ? currentRoleId
      : 4;

    setSelectedPlanRoleId(defaultRoleId);
  }, [currentRoleId, pricingPlans]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get("success") === "true") {
      addNotification(
        "Transaction réussie. Cliquez sur « Rafraîchir la formule » pour mettre à jour votre compte.",
        "success"
      );
      navigate("/formules-tarifs", { replace: true });
    }

    if (searchParams.get("canceled") === "true") {
      addNotification("Transaction annulée.", "warning");
      navigate("/formules-tarifs", { replace: true });
    }
  }, [location, addNotification, navigate]);

  const scrollPlans = (direction) => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: direction === "right" ? 340 : -340,
      behavior: "smooth",
    });
  };

  const handlePurchase = async (plan) => {
    if (!userId) {
      return addNotification(
        "Veuillez vous connecter pour souscrire à une formule.",
        "error"
      );
    }

    if (!plan.product_id || !plan.amount) {
      return addNotification("Cette formule n'est pas payable en ligne.", "error");
    }

    try {
      setLoading(true);

      const checkoutUrl = await createCheckoutSessionForPlan(
        {
          role_id: plan.role_id,
          formule: plan.formule,
          price: String(plan.amount),
          product_id: plan.product_id,
        },
        userId
      );

      const token = localStorage.getItem("token");
      if (token && effectiveUser) {
        const message = `L'utilisateur ${effectiveUser.prenom || ""} ${
          effectiveUser.nom || ""
        } a tenté de souscrire à la formule ${plan.formule} pour ${plan.price}${plan.period}.`;
        void sendNotificationToAdmin(message, token);
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);
      addNotification(
        "Erreur lors de la création de la session de paiement. Veuillez réessayer.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!userId) {
      addNotification(
        "Impossible de rafraîchir vos données. Veuillez vous reconnecter.",
        "error"
      );
      return;
    }

    try {
      setLoading(true);

      const updatedUser = await getUserById(userId);
      if (!updatedUser) {
        addNotification("Utilisateur non trouvé.", "error");
        return;
      }

      const mergedUser = {
        ...effectiveUser,
        ...updatedUser,
        userId: updatedUser.userId || updatedUser.id || effectiveUser?.userId,
      };

      updateUser(mergedUser);
      localStorage.setItem("userInfo", JSON.stringify(mergedUser));

      addNotification("Votre formule a été mise à jour avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors du rafraîchissement :", error);
      addNotification(
        "Erreur lors du rafraîchissement des données. Veuillez réessayer.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getPlanAction = (plan) => {
    const targetRank = PLAN_ORDER[plan.role_id] ?? -1;

    if (!isAuthenticated) {
      return {
        label: plan.role_id === 7 ? "Créer un compte gratuit" : "Créer un compte",
        disabled: false,
        helper:
          plan.role_id === 7
            ? "Commencez gratuitement"
            : "Inscription requise avant souscription",
        onClick: () => navigate("/register"),
      };
    }

    if (currentRoleId === 1) {
      return {
        label: "Compte administrateur",
        disabled: true,
        helper: "Aucune souscription nécessaire",
        onClick: null,
      };
    }

    if (currentRoleId === 6) {
      return {
        label: "Réservé au compte club",
        disabled: true,
        helper: "L'abonnement est géré par le club",
        onClick: null,
      };
    }

    if (currentRoleId === plan.role_id) {
      return {
        label: "Formule actuelle",
        disabled: true,
        helper: "Vous utilisez déjà cette formule",
        onClick: null,
      };
    }

    if (!plan.product_id) {
      if (currentPlanRank > targetRank) {
        return {
          label: "Formule inférieure",
          disabled: true,
          helper: "Votre formule actuelle est plus avancée",
          onClick: null,
        };
      }

      return {
        label: "Formule gratuite",
        disabled: true,
        helper: "Choisie lors de la création du compte",
        onClick: null,
      };
    }

    if (currentPlanRank > targetRank) {
      return {
        label: "Formule inférieure",
        disabled: true,
        helper: "Vous disposez déjà d'une formule supérieure",
        onClick: null,
      };
    }

    return {
      label: loading ? "Chargement..." : "Souscrire",
      disabled: loading,
      helper: "Paiement annuel sécurisé",
      onClick: () => handlePurchase(plan),
    };
  };

  return (
    <div className="w-full bg-slate-50 pb-16 pt-[110px] text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
        <section className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">
              Tarifs annuels
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">
              Choisissez votre formule
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
              Vous débutez sur la version gratuite ou en mode Démo. Vous pouvez ensuite passer à une formule payante en fonction de la taille de votre club et de vos besoins. Abonnement renouvelable chaque année, sans engagement de durée. Vous pouvez changer de formule à tout moment.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {isAuthenticated ? (
              <>
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  {effectiveUser?.photoURL ? (
                    <img
                      src={effectiveUser.photoURL}
                      alt={`${effectiveUser?.prenom || ""} ${effectiveUser?.nom || ""}`}
                      className="h-8 w-8 rounded-xl object-cover"
                    />
                  ) : (
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                      <FaUserCircle />
                    </span>
                  )}
                  <span className="max-w-[220px] truncate">
                    {currentPlan
                      ? `Formule actuelle : ${currentPlan.name}`
                      : `Compte : ${currentRoleLabel}`}
                  </span>
                </div>

                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold transition",
                    loading
                      ? "cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                      : "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                  )}
                >
                  <FaSyncAlt />
                  {loading ? "Chargement..." : "Rafraîchir"}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                >
                  Se connecter
                </button>
                <button
                  onClick={() => navigate("/register")}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  Créer un compte
                  <FaArrowRight />
                </button>
              </>
            )}
          </div>
        </section>

        <section className="relative">
          <div className="mb-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
              <FaStar />
              Faites défiler les formules
            </div>

            <div className="hidden items-center gap-2 md:flex">
              <button
                onClick={() => scrollPlans("left")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={() => scrollPlans("right")}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="flex snap-x snap-mandatory gap-5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {pricingPlans.map((plan) => {
              const Icon = plan.icon;
              const tone = TONE_STYLES[plan.tone];
              const action = getPlanAction(plan);
              const isCurrent = currentRoleId === plan.role_id;
              const isSelected = selectedPlanRoleId === plan.role_id;

              return (
                <article
                  key={plan.role_id}
                  className={cn(
                    "min-w-[280px] max-w-[280px] snap-start rounded-[28px] border p-5 shadow-sm transition-all sm:min-w-[300px] sm:max-w-[300px]",
                    tone.surface,
                    plan.featured && tone.outline,
                    isSelected && "translate-y-[-4px] shadow-xl"
                  )}
                >
                  <div className="flex h-full flex-col">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <span
                        className={cn(
                          "inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide",
                          tone.badge
                        )}
                      >
                        {isCurrent ? "Actuelle" : plan.badge}
                      </span>

                      <div
                        className={cn(
                          "flex h-11 w-11 items-center justify-center rounded-2xl text-lg",
                          tone.icon
                        )}
                      >
                        <Icon />
                      </div>
                    </div>

                    <h3 className="text-2xl font-black tracking-tight">
                      {plan.name}
                    </h3>

                    <div className="mt-4 flex items-end gap-2">
                      <span className={cn("text-4xl font-black leading-none", tone.price)}>
                        {plan.price}
                      </span>
                      {plan.period ? (
                        <span className="pb-1 text-sm text-slate-500 dark:text-slate-400">
                          {plan.period}
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-4 min-h-[44px] text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {plan.shortDescription}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {plan.highlights.map((item) => (
                        <span
                          key={item}
                          className={cn(
                            "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold",
                            tone.soft
                          )}
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    <div className="mt-6 flex-1" />

                    <button
                      onClick={action.onClick || undefined}
                      disabled={action.disabled}
                      className={cn(
                        "inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 font-semibold transition",
                        action.disabled
                          ? "cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-500"
                          : tone.button
                      )}
                    >
                      {action.label}
                    </button>

                    <button
                      onClick={() => setSelectedPlanRoleId(plan.role_id)}
                      className="mt-3 inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 bg-white/70 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-white dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-100 dark:hover:bg-slate-900"
                    >
                      Voir les détails
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* {selectedPlan ? (
          <section className="mt-8 rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={cn(
                      "inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide",
                      TONE_STYLES[selectedPlan.tone].badge
                    )}
                  >
                    {selectedPlan.badge}
                  </span>

                  {currentRoleId === selectedPlan.role_id ? (
                    <span className="inline-flex rounded-full bg-slate-900 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white dark:bg-slate-100 dark:text-slate-900">
                      Formule actuelle
                    </span>
                  ) : null}
                </div>

                <h2 className="mt-4 text-3xl font-black tracking-tight">
                  {selectedPlan.name}
                </h2>

                <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-400">
                  {selectedPlan.shortDescription}
                </p>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {selectedPlan.highlights.map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 dark:border-slate-800 dark:bg-slate-800/70 dark:text-slate-200"
                    >
                      {item}
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Inclus dans la formule
                  </h3>

                  <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                    {selectedPlan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-800/70 dark:text-slate-200"
                      >
                        <FaCheckCircle className="mt-0.5 shrink-0 text-emerald-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="w-full rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60 lg:max-w-[320px]">
                <div className="flex items-end gap-2">
                  <span
                    className={cn(
                      "text-4xl font-black leading-none",
                      TONE_STYLES[selectedPlan.tone].price
                    )}
                  >
                    {selectedPlan.price}
                  </span>
                  {selectedPlan.period ? (
                    <span className="pb-1 text-sm text-slate-500 dark:text-slate-400">
                      {selectedPlan.period}
                    </span>
                  ) : null}
                </div>

                <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
                  {selectedPlan.footer}
                </p>

                <div className="mt-5">
                  {(() => {
                    const action = getPlanAction(selectedPlan);
                    return (
                      <>
                        <button
                          onClick={action.onClick || undefined}
                          disabled={action.disabled}
                          className={cn(
                            "inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 font-semibold transition",
                            action.disabled
                              ? "cursor-not-allowed bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-500"
                              : TONE_STYLES[selectedPlan.tone].button
                          )}
                        >
                          {action.label}
                        </button>

                        <p className="mt-3 text-center text-xs leading-5 text-slate-500 dark:text-slate-400">
                          {action.helper}
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </section>
        ) : null} */}

        <section className="mt-8 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-black">Simple et lisible</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Commencez gratuitement avec Presence Free, puis passez sur une
                formule payante en fonction de vos besoins. Abonnement renouvelable chaque année, sans engagement de durée. Vous pouvez changer de formule à tout moment.
              </p>
            </div>

            {!isAuthenticated ? (
              <button
                onClick={() => navigate("/register")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Créer un compte
                <FaArrowRight />
              </button>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}

export default PricingPage;